import { useTranslation } from 'react-i18next';
import { type ReactNode, useContext, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Badge, Banner, Button, Text } from '@figma/fpl-components';
import { Icon16Checkmark, Icon16Close, Icon16Effects } from '@figma/fpl-icons';
import fplTokens from '@figma/fpl-tokens/css-vars';
import * as stylex from '@stylexjs/stylex';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { TextLgRg, TextSmRg, Typography, typographyStyles } from '@/UI/AppTypography/AppTypography';
import { formatPrice, MAX_LAUNCH_PROMOTION } from '@/consts/pricing.consts';
import { DATE_FORMATS } from '@/consts/date.consts';
import { log } from '@/logger/logger.ts';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType, BillingCycle } from '@/types/shared';
import useWorkspacesStore from '@/state/workspaces.state';
import { CreditsContext } from '@/services/CreditsContext';
import TeamSizeModal from '@/components/SubscriptionsAndPayments/TeamSizeModal';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { User } from '@/types/auth.types';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import {
  pricingDisabledActionStyles,
  maxTierAccentStyles,
  maxTierBadgeStyles,
  maxTierButtonStyles,
} from './pricing.styles';
import type { ApiObject } from '@/types/analytics.types';

const fplTypographyStyles = stylex.create({
  display: stylex.env.mixin.fpl.typography.textDisplay,
});

const logger = log.getLogger('PricingPackage');

// Optically centers the asterisk against the first line of credit text.
const ASTERISK_ICON_STYLE = { position: 'relative', top: -1 } as const;

export interface PricingFeature {
  text: string;
  isUnavailable?: boolean;
}

interface PricingPackageProps {
  subscriptionType: SubscriptionType;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  annualTotal: number;
  firstInvoicePrices?: {
    monthly: number;
    yearly: number;
  };
  credits: string;
  creditsInfo: string;
  featuresTitle?: string;
  features: PricingFeature[];
  highlightedFeatureIndex?: number;
  currentUserPlan: SubscriptionType;
  currentUserBillingCycle: BillingCycle;
  isPopular?: boolean;
  billingCycle: BillingCycle;
  workspaceId?: string;
  downgradeData?: {
    date?: Date;
    plan?: string;
  };
  currentUser?: User | undefined;
  isDiscountActive?: boolean;
  /** True when at least one card in the current view shows the discount badge, so the cards that
   *  don't reserve the matching height and stay aligned. A discounted card renders it regardless. */
  reserveDiscountSpace?: boolean;
  isMaxTierLayout?: boolean;
  analyticsContext?: ApiObject;
}

interface PricingPackagePresentationProps {
  subscriptionType: SubscriptionType;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  credits: string;
  creditsInfo: string;
  featuresTitle?: string;
  features: PricingFeature[];
  highlightedFeatureIndex?: number;
  billingCycle: BillingCycle;
  downgradeData?: PricingPackageProps['downgradeData'];
  reserveDiscountSpace?: boolean;
  isLoading: boolean;
  isThisDowngradedPlan: boolean;
  shouldDisablePackageButton: boolean;
  shouldHighlightPackage: boolean;
  discountLine: string | null;
  isDiscountVisible: boolean;
  buttonText: string;
  onPackageButtonClick: () => Promise<void>;
  startIcon: ReactNode;
  teamSizeModal: ReactNode;
  isMaxTierLayout: boolean;
}

/** Two lines of campaign copy plus vertical padding, shared by every card in the row. */
export const DISCOUNT_BADGE_MIN_HEIGHT = '48px';

/**
 * The badge is a plain Box rather than a typography component because AppTypography drops font
 * properties from `sx` on the FPL path, and the design's 11/400/16 does not match an FPL variant.
 */
const DISCOUNT_BADGE_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  width: '100%',
  minHeight: DISCOUNT_BADGE_MIN_HEIGHT,
  p: 1,
  color: 'var(--weave-pricing-badge-text)',
  fontSize: '11px',
  lineHeight: '16px',
  fontWeight: 400,
  backgroundColor: 'var(--weave-pricing-promotion-background)',
} as const;

/**
 * Whether the campaign applies to this card. It is for new paid subscriptions only: the server
 * discounts the checkout path and charges an upgrade from an existing paid plan in full. A
 * workspace reaches checkout exactly when it has no paid subscription yet, so a Free current plan
 * is the whole condition — `upgradePaidSubscription` rejects a Free subscription outright.
 */
export const isDiscountEligiblePlan = (currentPlan: SubscriptionType, targetPlan: SubscriptionType): boolean =>
  currentPlan === SubscriptionType.Free && targetPlan !== SubscriptionType.Free;

export const PricingPackage = ({
  subscriptionType,
  description,
  monthlyPrice,
  annualPrice,
  annualTotal,
  firstInvoicePrices,
  credits,
  creditsInfo,
  featuresTitle,
  features,
  highlightedFeatureIndex,
  currentUserPlan,
  currentUserBillingCycle,
  isPopular,
  billingCycle,
  workspaceId,
  downgradeData,
  currentUser,
  isDiscountActive,
  reserveDiscountSpace,
  isMaxTierLayout = false,
  analyticsContext,
}: PricingPackageProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showTeamSizeModal, setShowTeamSizeModal] = useState(false);
  const { openConfirmationDialog } = useConfirmationDialogStore();
  const { track } = useAnalytics();
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('payment', 'upgrade');
  currentUrl.searchParams.set('plan', subscriptionType);
  if (analyticsContext?.entrypoint) {
    currentUrl.searchParams.set('entrypoint', String(analyticsContext.entrypoint));
  }
  const callbackUrl = currentUrl.toString();

  const { openUpgradeSubscriptionModal, openDowngradeSubscriptionModal } = useContext(CreditsContext);

  const planOrder = Object.values(SubscriptionType);
  const currentPlanIndex = planOrder.indexOf(currentUserPlan);
  const packagePlanIndex = planOrder.indexOf(subscriptionType);
  const isTierUpgrade = packagePlanIndex > currentPlanIndex;

  /** Billing cycle is ignored for the Free plan since it's $0 either way. */
  const isCurrentPlan =
    currentUserPlan === subscriptionType &&
    (subscriptionType === SubscriptionType.Free || billingCycle === currentUserBillingCycle);

  const getPricingButtonText = () => {
    if (!currentUser) {
      return subscriptionType === SubscriptionType.Free
        ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.FREE_PLAN_CTA)
        : billingCycle === BillingCycle.Yearly
          ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_ANNUAL_PLAN_CTA)
          : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA);
    }
    if (isCurrentPlan) {
      if (isDowngradedPlanExists) {
        return t(I18N_KEYS.PAYMENTS.PRICING_TABLE.RESTORE_PLAN);
      }
      return t(I18N_KEYS.PAYMENTS.PRICING_TABLE.CURRENT_PLAN_CTA);
    }
    if (isTierUpgrade) {
      return billingCycle === BillingCycle.Yearly
        ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_ANNUAL_PLAN_CTA)
        : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA);
    } else {
      return t(I18N_KEYS.PAYMENTS.PRICING_TABLE.CHANGE_PLAN_CTA);
    }
  };

  const onPackageButtonClick = async () => {
    track(
      'Pricing Table - Clicked_package_button',
      { package: subscriptionType, billingCycle: billingCycle, ...analyticsContext },
      TrackTypeEnum.BI,
    );
    if (!currentUser) {
      // if user is not logged in, we need to redirect to the login page
      window.location.href = '/signin';
      return;
    }
    try {
      const livePlan = useWorkspacesStore.getState().activeWorkspace.subscription?.type ?? currentUserPlan;
      localStorage.setItem('previousPlanBeforePayment', livePlan);
    } catch (e) {
      logger.warn('Failed snapshotting previous plan', e); // best-effort; never block checkout
    }
    if (currentUserPlan === SubscriptionType.Free) {
      // no subscription yet -> send directly to stripe
      if (subscriptionType === SubscriptionType.Team) {
        // in case of team plan, we need to show the team size modal
        setShowTeamSizeModal(true);
        return;
      }
      const checkoutSessionUrl = await getStripeCheckoutUrl();
      if (checkoutSessionUrl) {
        window.location.href = checkoutSessionUrl;
      }
    } else {
      if (isDowngradedPlanExists && currentUserPlan === subscriptionType) {
        openConfirmationDialog({
          title: t(I18N_KEYS.PAYMENTS.PRICING_TABLE.RESTORE_CONFIRMATION_TITLE),
          message: t(I18N_KEYS.PAYMENTS.PRICING_TABLE.RESTORE_CONFIRMATION_MESSAGE),
          confirmBtnText: t(I18N_KEYS.PAYMENTS.PRICING_TABLE.RESTORE_CONFIRMATION_CONFIRM_TEXT),
          width: 'sm',
          onConfirm: () => onRestoreConfirm(),
        });
        return;
      }
      // already have a subscription -> upgrade or downgrade
      const isSamePlanUpgradingToYearly =
        packagePlanIndex === currentPlanIndex &&
        currentUserBillingCycle !== BillingCycle.Yearly &&
        billingCycle === BillingCycle.Yearly;
      const isUpgrade = isTierUpgrade || isSamePlanUpgradingToYearly;

      if (isUpgrade) {
        openUpgradeSubscriptionModal({
          name: subscriptionType,
          billingCycle,
          price: billingCycle === BillingCycle.Monthly ? monthlyPrice : annualPrice,
          annualTotal,
          numberOfSeats: currentUser?.activeWorkspace?.subscription?.seatsLimit,
          entrypoint: analyticsContext?.entrypoint,
        });
      } else {
        openDowngradeSubscriptionModal({
          name: subscriptionType,
          billingCycle,
          price: billingCycle === BillingCycle.Monthly ? monthlyPrice : annualPrice,
          annualTotal,
        });
      }
    }
  };

  const onRestoreConfirm = async () => {
    try {
      await safeAxios.post(API_ROUTES.workspaces.subscriptionRestore({ workspaceId }));
      window.location.reload();
    } catch (e) {
      logger.error('Failed to restore plan', e);
    }
  };

  const getStripeCheckoutUrl = async (numberOfSeats: number = 1): Promise<string | null> => {
    setIsLoading(true);
    try {
      const response = await safeAxios.post<{
        checkoutSessionUrl: string;
      }>(API_ROUTES.workspaces.subscriptionCheckoutSession({ workspaceId }), {
        subscriptionType,
        billingCycle,
        callbackUrl,
        numberOfSeats,
      });
      return response.data.checkoutSessionUrl;
    } catch (error) {
      logger.error('Failed getting stripe checkout', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const StartIcon = (
    <FlexCenVer>{isLoading ? <CircularProgress size={16} sx={{ color: color.Black92 }} /> : null}</FlexCenVer>
  );

  const isThisDowngradedPlan = useMemo(
    () => Boolean(downgradeData?.plan === subscriptionType && downgradeData?.date),
    [downgradeData?.date, downgradeData?.plan, subscriptionType],
  );

  const isDowngradedPlanExists = useMemo(
    () => Boolean(downgradeData?.plan && downgradeData?.date),
    [downgradeData?.date, downgradeData?.plan],
  );

  const isUserLoggedIn = Boolean(currentUser);
  const hasNoPendingDowngrade = !isDowngradedPlanExists;

  const shouldDisablePackageButton = isUserLoggedIn && isCurrentPlan && hasNoPendingDowngrade;

  const shouldHighlightPackage = Boolean(isPopular && (currentUserPlan === SubscriptionType.Free || !currentUserPlan));

  const discountLine =
    firstInvoicePrices === undefined
      ? null
      : billingCycle === BillingCycle.Monthly
        ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.DISCOUNT.FIRST_MONTH, {
            discounted: formatPrice(firstInvoicePrices.monthly),
            full: formatPrice(monthlyPrice),
          })
        : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.DISCOUNT.FIRST_YEAR, {
            discounted: formatPrice(firstInvoicePrices.yearly),
            full: formatPrice(annualTotal),
          });

  const isDiscountVisible =
    Boolean(isDiscountActive) && discountLine !== null && isDiscountEligiblePlan(currentUserPlan, subscriptionType);

  const buttonText =
    isThisDowngradedPlan && downgradeData?.date
      ? `${t(I18N_KEYS.PAYMENTS.PRICING_TABLE.DOWNGRADE_DATE_PREFIX)} ${format(downgradeData.date, DATE_FORMATS.DATE)}`
      : getPricingButtonText();

  const teamSizeModal = showTeamSizeModal ? (
    <TeamSizeModal
      open={showTeamSizeModal}
      onClose={() => setShowTeamSizeModal(false)}
      packagePrice={billingCycle === BillingCycle.Monthly ? monthlyPrice : annualPrice}
      annualTotal={annualTotal}
      billingCycle={billingCycle}
      isProcessing={isLoading}
      getStripeCheckoutUrl={getStripeCheckoutUrl}
      isDiscountActive={isDiscountVisible}
    />
  ) : null;

  const presentationProps: PricingPackagePresentationProps = {
    subscriptionType,
    description,
    monthlyPrice,
    annualPrice,
    credits,
    creditsInfo,
    featuresTitle,
    features,
    highlightedFeatureIndex,
    billingCycle,
    downgradeData,
    reserveDiscountSpace,
    isLoading,
    isThisDowngradedPlan,
    shouldDisablePackageButton,
    shouldHighlightPackage,
    discountLine,
    isDiscountVisible,
    buttonText,
    onPackageButtonClick,
    startIcon: StartIcon,
    teamSizeModal,
    isMaxTierLayout,
  };

  return <FplPricingPackage {...presentationProps} />;
};

const FplPricingPackage = ({
  subscriptionType,
  description,
  monthlyPrice,
  annualPrice,
  credits,
  creditsInfo,
  featuresTitle,
  features,
  highlightedFeatureIndex,
  billingCycle,
  reserveDiscountSpace,
  isLoading,
  isThisDowngradedPlan,
  shouldDisablePackageButton,
  shouldHighlightPackage,
  discountLine,
  isDiscountVisible,
  buttonText,
  onPackageButtonClick,
  startIcon,
  teamSizeModal,
  isMaxTierLayout,
}: PricingPackagePresentationProps) => {
  const { t } = useTranslation();
  const isTeamLayout = isMaxTierLayout && subscriptionType === SubscriptionType.Team;
  const packageDisplayName = subscriptionType === SubscriptionType.Pro ? 'Professional' : subscriptionType;
  // The small price label following the amount, such as “/ month” or “/ user / month”.
  const priceSuffix =
    subscriptionType !== SubscriptionType.Free ? (
      <FlexCol
        data-testid="pricing-price-suffix"
        style={{ height: isMaxTierLayout ? 40 : 32, justifyContent: 'flex-end' }}
        sx={{ flex: '1 1 0', minWidth: 0, pb: isMaxTierLayout ? fplTokens.spacer.x2 : undefined }}
      >
        <Text.span variant="body-medium" color="secondary">
          {subscriptionType === SubscriptionType.Team
            ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PRICE_SUFFIX_USER_MONTH)
            : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PRICE_SUFFIX_MONTH)}
        </Text.span>
      </FlexCol>
    ) : null;

  const creditsSection = (
    <Flex
      data-testid="pricing-credits"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: fplTokens.spacer.x4,
      }}
    >
      <Flex
        sx={{
          gap: fplTokens.spacer.x1,
          color: fplTokens.color.text,
          '--fpl-icon-color': fplTokens.color.icon,
          ...(shouldHighlightPackage ? { '--fpl-icon-color': 'var(--weave-pricing-highlight-icon)' } : {}),
        }}
      >
        <AsteriskIcon style={ASTERISK_ICON_STYLE} />
        <FlexCol sx={{ gap: fplTokens.spacer.x1 }}>
          <TextLgRg>{credits}</TextLgRg>
          <TextSmRg color={fplTokens.color.textSecondary}>{creditsInfo}</TextSmRg>
        </FlexCol>
      </Flex>
    </Flex>
  );

  const planCta = (
    <Box
      data-testid="pricing-cta"
      sx={{
        mb: fplTokens.spacer.x4,
        ...(isMaxTierLayout
          ? maxTierAccentStyles
          : shouldHighlightPackage
            ? {
                '--color-bg-brand': color.Yellow100,
                '--color-bg-brand-hover': color.Yellow64,
                '--color-bg-brand-pressed': color.Yellow40,
                '--color-icon-onbrand': color.Black100,
                '--color-text-onbrand': color.Black100,
              }
            : {}),
      }}
    >
      {shouldDisablePackageButton ? (
        <Button variant="primary" size="lg" width="fill" disabled onClick={() => void onPackageButtonClick()}>
          {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.CURRENT_PLAN_CTA)}
        </Button>
      ) : (
        <ButtonContained
          disabled={shouldDisablePackageButton || isLoading || isThisDowngradedPlan}
          fullWidth
          mode={!isMaxTierLayout && !shouldHighlightPackage ? 'outlined' : undefined}
          sx={isMaxTierLayout ? maxTierButtonStyles : undefined}
          onClick={() => void onPackageButtonClick()}
          startIcon={isLoading ? startIcon : undefined}
        >
          {buttonText}
        </ButtonContained>
      )}
    </Box>
  );

  let packageBackgroundColor: string = isTeamLayout
    ? 'var(--weave-pricing-team-background)'
    : fplTokens.color.bgElevated;
  if (shouldHighlightPackage) {
    packageBackgroundColor = fplTokens.color.bg;
  }

  return (
    <FlexCol
      data-testid="pricing-package-container"
      sx={{
        ...pricingDisabledActionStyles,
        '--color-bg-disabled': isTeamLayout ? undefined : 'var(--weave-pricing-disabled-background)',
        width: '100%',
        maxWidth: isMaxTierLayout ? undefined : '320px',
        minHeight: isTeamLayout ? '328px' : isMaxTierLayout ? '496px' : '588px',
        ...(isTeamLayout
          ? {
              display: { xs: 'flex', sm: 'grid' },
              gridTemplateColumns: '1fr 1fr',
              columnGap: '24px',
              pb: '32px',
              '& > [data-plan-heading]': { gridColumn: '1 / -1' },
              '& > [data-plan-features]': { gridColumn: 2, gridRow: '2 / 5' },
              '& > [data-testid="pricing-cta"]': { gridColumn: 1 },
            }
          : {}),
        backgroundColor: packageBackgroundColor,
        border: 'none',
        outline: `1px solid ${fplTokens.color.border}`,
        borderRadius: fplTokens.radius.large,
        p: fplTokens.spacer.x5,
        px: isMaxTierLayout && !isTeamLayout ? fplTokens.spacer.x4 : undefined,
        pt: isMaxTierLayout ? fplTokens.spacer.x5 : fplTokens.spacer.x6,
        position: 'relative',
        ...(shouldHighlightPackage
          ? {
              '--fpl-badge-color': 'var(--weave-pricing-badge-text)',
              '--fpl-badge-border-color': fplTokens.ramp.lime600,
            }
          : {}),
        '& > [data-recommended-badge]': {
          position: 'absolute',
          top: fplTokens.spacer.x3,
          right: fplTokens.spacer.x3,
        },
      }}
    >
      <FlexCol data-plan-heading="" sx={{ gap: fplTokens.spacer.x2, mb: fplTokens.spacer.x4 }}>
        <Typography
          component="h2"
          variant="unstyled"
          sx={{ textTransform: 'capitalize' }}
          {...stylex.props(typographyStyles.textHeadingLarge)}
        >
          {packageDisplayName}
        </Typography>
        <Box sx={{ minHeight: isMaxTierLayout ? '22px' : '44px' }}>
          <Text.div variant="body-large" color="secondary">
            {description}
          </Text.div>
        </Box>
      </FlexCol>
      <Flex
        sx={{
          alignItems: isMaxTierLayout ? 'flex-end' : 'baseline',
          mb: fplTokens.spacer.x3,
          gap: fplTokens.spacer.x2,
          minHeight: '56px',
        }}
      >
        <Typography component="span" variant="unstyled" {...stylex.props(fplTypographyStyles.display)}>
          {formatPrice(billingCycle === BillingCycle.Monthly ? monthlyPrice : annualPrice)}
        </Typography>
        {priceSuffix}
      </Flex>
      {creditsSection}
      {(isDiscountVisible || reserveDiscountSpace) && (
        <Box
          data-testid="pricing-package-discount-badge"
          aria-hidden={!isDiscountVisible}
          sx={{
            ...DISCOUNT_BADGE_STYLE,
            borderRadius: fplTokens.radius.medium,
            visibility: isDiscountVisible ? 'visible' : 'hidden',
          }}
        >
          {isDiscountVisible ? discountLine : ''}
        </Box>
      )}
      {planCta}

      {!isTeamLayout && (
        <Box sx={{ mb: fplTokens.spacer.x4, width: '100%' }}>
          <AppDivider />
        </Box>
      )}
      <FlexCol data-plan-features="" sx={{ gap: fplTokens.spacer.x2 }}>
        {featuresTitle && (
          <Text.span variant="body-large-strong" color="default">
            {featuresTitle}
          </Text.span>
        )}
        {features.map((feature, index) => {
          const isUnavailableFeature = subscriptionType === SubscriptionType.Free && feature.isUnavailable;
          const isGainFeature = subscriptionType === SubscriptionType.Pro && index === highlightedFeatureIndex;
          return (
            <Flex key={index} data-testid="pricing-feature-row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <Box
                data-testid="pricing-feature-icon-wrapper"
                style={{ display: 'flex', width: 16, height: 16, flex: '0 0 16px', marginTop: 3 }}
                sx={isUnavailableFeature ? { '--fpl-icon-color': fplTokens.color.iconSecondary } : undefined}
              >
                {isUnavailableFeature ? (
                  <Icon16Close data-testid="unavailable-feature-icon" />
                ) : (
                  <Icon16Checkmark data-testid="available-feature-icon" />
                )}
              </Box>
              <Box
                data-testid="pricing-feature-text"
                style={{ flex: '1 1 0', minWidth: 0 }}
                sx={
                  isGainFeature && !isMaxTierLayout
                    ? {
                        '--fpl-badge-color': 'var(--weave-pricing-gain-badge-text)',
                        '--fpl-badge-border-color': fplTokens.ramp.grey400,
                      }
                    : undefined
                }
              >
                {isGainFeature ? (
                  <Text.span data-testid="gain-feature-line" variant="body-large" color="default">
                    {feature.text}{' '}
                    <Box component="span" sx={isMaxTierLayout ? maxTierBadgeStyles : undefined}>
                      <Badge
                        data-testid="gain-20-badge"
                        variant={isMaxTierLayout ? 'brandFilled' : 'brandOutline'}
                        size="md"
                      >
                        {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.GAIN_20_PERCENT)}
                      </Badge>
                    </Box>
                  </Text.span>
                ) : (
                  <Text.span variant="body-large" color={isUnavailableFeature ? 'secondary' : 'default'}>
                    {feature.text}
                  </Text.span>
                )}
              </Box>
            </Flex>
          );
        })}
      </FlexCol>
      {isMaxTierLayout &&
        subscriptionType === SubscriptionType.Max &&
        Date.now() <= MAX_LAUNCH_PROMOTION.signupDeadline.getTime() && (
          <Box
            data-testid="max-launch-promotion"
            sx={{
              mt: fplTokens.spacer.x2,
              '--color-bg-brand-tertiary': 'var(--weave-pricing-promotion-background)',
              '--color-text': 'var(--weave-pricing-promotion-text)',
              '--fpl-icon-color': 'var(--weave-pricing-promotion-text)',
            }}
          >
            <Banner.InsetModal variant="brand" icon={<Icon16Effects aria-hidden />}>
              <Banner.Message>
                {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.MAX_LAUNCH_PROMOTION, {
                  discountPercent: MAX_LAUNCH_PROMOTION.discountPercent,
                  model: MAX_LAUNCH_PROMOTION.model,
                  signupDeadline: new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  }).format(MAX_LAUNCH_PROMOTION.signupDeadline),
                })}
              </Banner.Message>
            </Banner.InsetModal>
          </Box>
        )}
      {shouldHighlightPackage && (
        <Box data-recommended-badge="" sx={isMaxTierLayout ? maxTierBadgeStyles : undefined}>
          <Badge
            data-testid="recommended-badge"
            variant={isMaxTierLayout ? 'brandFilled' : 'brandOutline'}
            size={isMaxTierLayout ? 'lg' : 'md'}
          >
            {t(
              isMaxTierLayout
                ? I18N_KEYS.PAYMENTS.PRICING_TABLE.BEST_VALUE
                : I18N_KEYS.PAYMENTS.PRICING_TABLE.RECOMMENDED,
            )}
          </Badge>
        </Box>
      )}

      {teamSizeModal}
    </FlexCol>
  );
};
